/*
 * TaxRateForm.tsx : Un formulaire pour ajouter ou modifier un taux de taxe.
 */

// Import React
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// Import FontAwesome
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPercent } from '@fortawesome/free-solid-svg-icons';

// Import Communs
import {
	finishFormSave,
	FormSaveActions,
	InputField,
	ToggleButtonField,
	useFormDirty,
} from '../../../common';

// Import Tax Rate
import { TaxRateData } from '../types/types';
import {
	createTaxRate,
	getTaxRateById,
	updateTaxRate,
} from '../services/taxRateApi';

// Composant TaxRateForm
const TaxRateForm: React.FC = () => {
	// Hooks de State
	const [loading, setLoading] = React.useState(true);
	const [baselineReady, setBaselineReady] = React.useState(false);
	const [formData, setFormData] = React.useState<TaxRateData>({
		id: 0,
		code: '',
		label: '',
		rate: 0,
		country: '',
		isActive: true,
	});

	// Hook de Navigation et Paramètres de navigation
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { isDirty, markClean } = useFormDirty(formData, baselineReady);

	// Une fois le composant monté et si un id est présent, fetch du taux de taxe et préremplissage du formulaire
	React.useEffect(() => {
		if (id) {
			const token = localStorage.getItem('token');
			if (!token) return;

			getTaxRateById(Number(id), token)
				.then((data) => setFormData(data))
				.catch((error) => console.error(error))
				.finally(() => {
					setLoading(false);
					setBaselineReady(true);
				});
		} else {
			// Si pas d'id, on arrête le loading directement
			setLoading(false);
			setBaselineReady(true);
		}
	}, [id]);

	// Gestion des changements dans le formulaire
	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	// Gestion de la soumission du formulaire
	const [isSubmitting, setIsSubmitting] = React.useState(false);
	const handleSave = (shouldLeave: boolean) => {
		const token = localStorage.getItem('token');
		if (!token) return;
		// Indiquer que la soumission est en cours
		setIsSubmitting(true);
		const onLeave = () => navigate('/app/settings/tva');
		if (id) {
			updateTaxRate(Number(id), formData, token)
				.then(() => finishFormSave(shouldLeave, { onLeave, markClean }))
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		} else {
			createTaxRate(formData, token)
				.then((created) =>
					finishFormSave(shouldLeave, {
						onLeave,
						markClean,
						stayOnEdit: created?.id
							? () =>
									navigate(`/app/taxrates/${created.id}/edit`, {
										replace: true,
									})
							: undefined,
					})
				)
				.catch((error) => console.error(error))
				.finally(() => setIsSubmitting(false));
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		handleSave(true);
	};

	return (
		<div>
			{/* CARD PRINCIPALE */}
			<div className="max-w-3xl mx-auto p-6 bg-white rounded-lg shadow-md">
				{/* HEADER DE LA CARD */}
				<div className="flex items-start mb-3 space-x-4">
					{/* Icône à gauche */}
					<FontAwesomeIcon
						icon={faPercent}
						className="w-20 h-20 text-blue-900/90"
					/>

					{/* Titre + toggle à droite */}
					<div className="flex flex-col mt-3">
						<h1 className="text-3xl font-bold">
							{id ? "Édition d'un taux de taxe" : 'Nouveau taux de taxe'}
						</h1>

						{/* Toggle “Actif” sous le titre */}
						<div className="mt-2">
							<ToggleButtonField
								label="Actif"
								isOn={formData.isActive}
								onChange={(value) =>
									setFormData({ ...formData, isActive: value })
								}
								invert={true}
								size="sm"
								loading={loading}
							/>
						</div>
					</div>
				</div>

				{/* CONTENU DE LA CARD */}
				<div className="items-center space-x-4 mt-3 w-full">
					<form onSubmit={handleSubmit} className="space-y-4">
						{/* 2 COLONNES */}
						<div className="grid grid-cols-1 md:grid-cols-2 gap-16">
							{/* COLONNE GAUCHE */}
							<div className="space-y-4">
								<InputField
									label="Code"
									name="code"
									value={formData.code}
									onChange={handleChange}
									required
									loading={loading}
									placeholder='ex: "TVA_FR_20"'
								/>
								<InputField
									label="Label"
									name="label"
									value={formData.label}
									onChange={handleChange}
									required
									loading={loading}
									placeholder='ex: "TVA France 20%"'
								/>
							</div>

							{/* COLONNE DROITE */}
							<div className="space-y-4">
								<InputField
									label="Taux de taxe"
									name="rate"
									value={formData.rate}
									onChange={handleChange}
									required
									loading={loading}
									placeholder='ex: "0.20" pour 20%'
								/>
								<InputField
									label="Pays"
									name="country"
									value={formData.country}
									onChange={handleChange}
									required
									loading={loading}
									placeholder='ex: "FR" pour France'
								/>
							</div>
						</div>
					</form>
					<FormSaveActions
						isDirty={isDirty}
						isSubmitting={isSubmitting}
						onCancel={() => navigate('/app/settings/tva')}
						onSave={() => handleSave(false)}
						onSaveAndLeave={() => handleSave(true)}
					/>
				</div>
			</div>
		</div>
	);
};

export default TaxRateForm;
